import { Diagnostic } from "@fairfleet/geotab";
import CloseIcon from "@mui/icons-material/Close";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import debounce from "lodash/debounce";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDiagnosticsBySearch } from "@/hooks/diagnostic/useDiagnosticsBySearch";
import { DiagnosticView, ViewColumn } from "@/models/DiagnosticView";

interface Props {
  open: boolean;
  view: DiagnosticView;
  onClose: () => void;
  onSubmit: (columns: ViewColumn[]) => void;
}

function moveItem<T>(arr: T[], from: number, to: number): T[] {
  if (from === to) return arr;
  const copy = arr.slice();
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item);
  return copy;
}

export function ManageColumnsDialog({ open, view, onClose, onSubmit }: Props) {
  const { t } = useTranslation();
  const [columns, setColumns] = useState<ViewColumn[]>([]);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Diagnostic[]>([]);
  const { data: diagnostics, isFetching } = useDiagnosticsBySearch(query, !open, 50);

  useEffect(() => {
    if (open) {
      setColumns(view.columns ?? []);
      setSelected([]);
      setQuery("");
      setDragIndex(null);
    }
  }, [open, view.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = useMemo(() => debounce((q: string) => setQuery(q), 300), []);

  const existingIds = new Set(columns.map(c => c.diagnosticId));
  const filteredOptions = (diagnostics ?? []).filter(d => !existingIds.has(d.id));

  const handleAdd = (_: unknown, value: Diagnostic[]) => {
    const newCols: ViewColumn[] = value
      .filter(d => !!d.id)
      .map(d => ({ diagnosticId: d.id as string, diagnosticName: d.name ?? "" }));
    setColumns(prev => [...prev, ...newCols]);
    setSelected([]);
    setQuery("");
  };

  const handleRemove = (index: number) => {
    setColumns(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("views.manageColumns.title", { name: view.name })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <div>
            <Typography variant="subtitle2" gutterBottom>
              {t("views.manageColumns.currentSection")}
            </Typography>
            {columns.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("views.manageColumns.noColumns")}
              </Typography>
            ) : (
              <List dense disablePadding>
                {columns.map((col, index) => (
                  <ListItem
                    key={col.diagnosticId + index}
                    draggable
                    onDragStart={() => setDragIndex(index)}
                    onDragOver={e => e.preventDefault()}
                    onDrop={() => {
                      if (dragIndex !== null) {
                        setColumns(prev => moveItem(prev, dragIndex, index));
                        setDragIndex(null);
                      }
                    }}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      mb: 0.5,
                      cursor: "grab",
                      opacity: dragIndex === index ? 0.5 : 1,
                    }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleRemove(index)}
                        aria-label={t("common.close")}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <DragHandleIcon sx={{ mr: 1, color: "text.secondary" }} />
                    <ListItemText primary={col.diagnosticName} secondary={col.diagnosticId} />
                  </ListItem>
                ))}
              </List>
            )}
          </div>
          <div>
            <Typography variant="subtitle2" gutterBottom>
              {t("views.manageColumns.addSection")}
            </Typography>
            <Autocomplete
              multiple
              options={filteredOptions}
              value={selected}
              onChange={handleAdd}
              getOptionLabel={o => o.name ?? ""}
              getOptionKey={o => o.id ?? ""}
              loading={isFetching}
              filterSelectedOptions
              onInputChange={(_, v) => handleSearch(v)}
              disableCloseOnSelect
              renderOption={(props, option, { selected: s }) => (
                <li {...props} key={option.id}>
                  <Checkbox checked={s} style={{ marginRight: 8 }} />
                  <div>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {"ID: " + option.id}
                    </Typography>
                  </div>
                </li>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  label={t("views.manageColumns.search")}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isFetching ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
          </div>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
        <Button variant="contained" onClick={() => { onSubmit(columns); onClose(); }}>
          {t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
